import React, { useEffect, useRef, useState } from 'react';
import { uiSounds } from '../../lib/uiSounds';
import {
  IconAward, IconCircleCheckBig, IconCoins, IconInfo, IconShieldAlert, IconSparkles, IconTriangleAlert,
} from './icons';
import './styles.scss';

type ToastType = 'info' | 'success' | 'warning' | 'danger' | 'gold' | 'quest' | 'level';

// Three levels, so what matters weighs more than what does not:
//  - quiet: small, bottom left, short, silent (a deposit, a catch, a refusal). The same message in a row
//    merges into one with a count, and only the last few stay.
//  - notable: the card at the top centre, with its sound (gold, quest, level, and the one-off warnings)
//  - alert: a band that stays while a state lasts (the fog), ended by the server, with a veil on the edges
//    of the screen that grows with the danger
type Tier = 'quiet' | 'notable' | 'alert';

interface ToastState {
  key: string;
  text: string;
  type: ToastType;
  tier: Tier;
  count: number;
  intensity: number;
}

interface ToastDetail {
  key?: string;
  text?: string;
  type?: string;
  silent?: boolean;
  sticky?: boolean;
  intensity?: number;
  clear?: boolean;
}

// How long a toast stays after its last update. The same key firing again refreshes the box in place instead
// of stacking a new one (vein states, a catch...).
const QUIET_MS = 2800;
const NOTABLE_MS = 4500;
const NOTABLE_URGENT_MS = 6500;
const MAX_QUIET = 3;
const MAX_NOTABLE = 3;

const TOAST_ICON: Record<ToastType, React.FC> = {
  info: IconInfo,
  success: IconCircleCheckBig,
  warning: IconTriangleAlert,
  danger: IconShieldAlert,
  gold: IconCoins,
  quest: IconSparkles,
  level: IconAward,
};

const tierOf = (type: ToastType, sticky: boolean): Tier => {
  if (sticky) return 'alert';
  return type === 'info' || type === 'success' ? 'quiet' : 'notable';
};

// A longer text stays longer, so it can be read: a little over its reading time, up to a cap
const READ_MS_PER_CHAR = 45;
const MAX_MS = 10000;

const durationOf = (tier: Tier, type: ToastType, text: string): number => {
  const base = tier === 'quiet' ? QUIET_MS : type === 'warning' || type === 'danger' ? NOTABLE_URGENT_MS : NOTABLE_MS;
  return Math.min(MAX_MS, Math.max(base, 1200 + text.length * READ_MS_PER_CHAR));
};

const playSound = (type: ToastType, tier: Tier) => {
  if (tier === 'quiet') return;
  if (type === 'gold') uiSounds.playGoldJingle();
  else if (type === 'quest' || type === 'level') uiSounds.playQuestFanfare();
  else uiSounds.playClick();
};

// Generic toast, server-driven via skymp5-client's toastService.ts (any system can trigger one through
// notify.ts's sendToast(), and a persistent alert through sendAlert()/clearAlert()). `type` is optional
// and cosmetic: existing callers that omit it keep the quiet "info" look.
const Toast: React.FC = () => {
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const drop = (key: string) => {
      const timer = timers.current.get(key);
      if (timer) clearTimeout(timer);
      timers.current.delete(key);
      setToasts((prev) => prev.filter((t) => t.key !== key));
    };

    const handler = (e: Event) => {
      const detail = ((e as CustomEvent).detail ?? {}) as ToastDetail;
      const key = detail.key;
      if (!key) return;
      if (detail.clear) {
        drop(key);
        return;
      }
      const text = detail.text;
      if (!text) return;
      const type: ToastType = detail.type && detail.type in TOAST_ICON ? (detail.type as ToastType) : 'info';
      const sticky = detail.sticky === true;
      const tier = tierOf(type, sticky);
      const intensity = Math.max(0, Math.min(1, typeof detail.intensity === 'number' ? detail.intensity : 0));

      setToasts((prev) => {
        const found = prev.find((t) => t.key === key);
        if (found) {
          const same = found.text === text && tier === 'quiet';
          return prev.map((t) => (t.key === key
            ? { ...t, text, type, tier, intensity, count: same ? t.count + 1 : 1 }
            : t));
        }
        if (!detail.silent) playSound(type, tier);
        const next = [...prev, { key, text, type, tier, count: 1, intensity }];
        // Only the most recent few of a level stay, the oldest give way
        const overflow = (t: Tier, max: number) => next.filter((x) => x.tier === t).slice(0, -max).map((x) => x.key);
        const gone = new Set([...overflow('quiet', MAX_QUIET), ...overflow('notable', MAX_NOTABLE)]);
        gone.forEach((k) => {
          const timer = timers.current.get(k);
          if (timer) clearTimeout(timer);
          timers.current.delete(k);
        });
        return next.filter((x) => !gone.has(x.key));
      });

      const existing = timers.current.get(key);
      if (existing) clearTimeout(existing);
      timers.current.delete(key);
      // A persistent alert has no timer: the server ends it
      if (!sticky) timers.current.set(key, setTimeout(() => drop(key), durationOf(tier, type, text)));
    };

    window.addEventListener('skymp5-client:toast', handler);
    const pending = timers.current;
    return () => {
      window.removeEventListener('skymp5-client:toast', handler);
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  if (toasts.length === 0) return null;

  const alerts = toasts.filter((t) => t.tier === 'alert');
  const notables = toasts.filter((t) => t.tier === 'notable');
  const quiets = toasts.filter((t) => t.tier === 'quiet');
  // The veil follows the heaviest alert: grey while it only warns, red once it hurts
  const veil = alerts.reduce<ToastState | null>((best, t) => (!best || t.intensity >= best.intensity ? t : best), null);

  return (
    <>
      {veil ? (
        <div
          className={`toast-veil toast-veil--${veil.type === 'danger' ? 'danger' : 'warning'}`}
          style={{ opacity: 0.25 + veil.intensity * 0.75 }}
          aria-hidden="true"
        />
      ) : null}

      {alerts.length + notables.length > 0 ? (
        <div className="toast-container">
          {alerts.map((t) => {
            const Icon = TOAST_ICON[t.type];
            return (
              <div className={`toast toast--${t.type} toast--alert`} key={t.key} role="alert">
                <span className="toast__icon"><Icon /></span>
                <span className="toast__text">{t.text}</span>
              </div>
            );
          })}
          {notables.map((t) => {
            const Icon = TOAST_ICON[t.type];
            return (
              <div className={`toast toast--${t.type}`} key={t.key}>
                <span className="toast__icon"><Icon /></span>
                <span className="toast__text">{t.text}</span>
              </div>
            );
          })}
        </div>
      ) : null}

      {quiets.length > 0 ? (
        <div className="toast-quiet">
          {quiets.map((t) => {
            const Icon = TOAST_ICON[t.type];
            return (
              <div className={`toast-quiet__item toast-quiet__item--${t.type}`} key={t.key}>
                <span className="toast-quiet__icon"><Icon /></span>
                <span className="toast-quiet__text">{t.text}</span>
                {t.count > 1 ? <span className="toast-quiet__count">x{t.count}</span> : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </>
  );
};

export default Toast;
